import { useCallback, useEffect, useMemo, useRef, useState } from "react"

type StopAccept = {
  accept(): Promise<File>
  stop(): Promise<void>
}

export type RecordingState =
  | { status: "idle"; start(): Promise<void> }
  | ({ status: "recording"; levels: number[]; pause(): void } & StopAccept)
  | ({ status: "paused"; levels: number[]; resume(): void } & StopAccept)
  | {
      status: "complete"
      audioFile: File
      start(): Promise<void>
      reset(): void
    }

/**
 * Simplified transcription hook using OpenAI Transcriptions API (gpt-4o-transcribe)
 * instead of the more complex Realtime API with WebRTC.
 *
 * This hook provides the same interface as useWorkTaskTranscriptionSession but:
 * - Uses MediaRecorder for local audio capture
 * - Transcribes on-demand when user clicks "Generate" (not real-time)
 * - 10x cheaper ($0.006/min vs $0.06/min)
 * - Simpler implementation (~100 lines vs ~650 lines)
 * - More reliable (no WebRTC connection issues)
 */
export function useWhisperTranscription(): RecordingState {
  const [isPaused, setIsPaused] = useState(false)
  const [isSessionActive, setIsSessionActive] = useState(false)
  const [levels, setAudioLevels] = useState<number[]>([0, 0, 0])
  const [audioFile, setAudioFile] = useState<File | null>(null)

  // Refs for media recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])

  // Refs for audio visualization
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const rafRef = useRef<number | null>(null)

  const cleanup = useCallback(() => {
    // Stop animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    // Stop all audio tracks and cleanup MediaRecorder
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop())
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop()
      }
      mediaRecorderRef.current = null
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }

    analyserRef.current = null
    audioChunksRef.current = []
    setAudioLevels([0, 0, 0])
    setIsSessionActive(false)
    setIsPaused(false)
  }, [])

  const start = useCallback(async () => {
    cleanup()
    setAudioFile(null)
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Setup MediaRecorder for audio capture
      // Try to use webm codec first, fall back to defaults
      let options: MediaRecorderOptions = {}
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        options = { mimeType: "audio/webm;codecs=opus" }
      } else if (MediaRecorder.isTypeSupported("audio/webm")) {
        options = { mimeType: "audio/webm" }
      }

      const recorder = new MediaRecorder(stream, options)

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      recorder.onerror = (event) => {
        console.error("MediaRecorder error:", event)
      }

      // Start recording, collecting chunks every second
      recorder.start(1000)
      mediaRecorderRef.current = recorder

      if (import.meta.env.DEV) {
        console.log(
          "🎙️ Audio recording started with MIME type:",
          recorder.mimeType,
        )
      }

      // Setup Web Audio API for live audio visualization
      try {
        const ctx = new (
          window.AudioContext || (window as any).webkitAudioContext
        )()
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 1024
        analyser.smoothingTimeConstant = 0.85
        source.connect(analyser)
        audioContextRef.current = ctx
        analyserRef.current = analyser

        const freqBins = new Uint8Array(analyser.frequencyBinCount)

        const tick = () => {
          if (!analyserRef.current) return
          // Only update levels when actively recording
          if (mediaRecorderRef.current?.state === "recording") {
            analyserRef.current.getByteFrequencyData(freqBins)
            const normalized = new Array(freqBins.length)
            for (let i = 0; i < freqBins.length; i++) {
              normalized[i] = freqBins[i] / 255
            }
            setAudioLevels(normalized)
          }
          rafRef.current = window.requestAnimationFrame(tick)
        }
        tick()
      } catch (err) {
        console.warn("Audio context/analyser setup failed:", err)
      }

      setIsSessionActive(true)
      setIsPaused(false)

      if (import.meta.env.DEV) {
        console.log("✅ Whisper recording session started")
      }
    } catch (error) {
      console.error("❌ Error starting recording session:", error)
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      throw new Error(`Failed to start recording: ${errorMessage}`)
    }
  }, [cleanup])

  const pause = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause()
      setIsPaused(true)
      if (import.meta.env.DEV) {
        console.log("⏸️ Recording paused")
      }
    }
  }, [])

  const resume = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume()
      setIsPaused(false)
      if (import.meta.env.DEV) {
        console.log("▶️ Recording resumed")
      }
    }
  }, [])

  const stop = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (
        !mediaRecorderRef.current ||
        mediaRecorderRef.current.state === "inactive"
      ) {
        cleanup()
        reject(new Error("No active recording to stop"))
        return
      }

      mediaRecorderRef.current.onstop = () => {
        cleanup()
        resolve()
      }

      mediaRecorderRef.current.stop()

      if (import.meta.env.DEV) {
        console.log("🛑 Recording session stopped and cleaned up")
      }
    })
  }, [cleanup])

  // Cleanup on unmount to prevent resource leaks
  useEffect(() => () => cleanup(), [cleanup])

  const reset = useCallback(() => {
    setAudioFile(null)
  }, [])

  const accept = useCallback((): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (
        !mediaRecorderRef.current ||
        mediaRecorderRef.current.state === "inactive"
      ) {
        cleanup()
        reject(new Error("No active recording to stop"))
        return
      }

      const mimeType =
        mediaRecorderRef.current.mimeType || "audio/webm;codecs=opus"

      mediaRecorderRef.current.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          cleanup()
          reject(new Error("No audio chunks recorded"))
          return
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })

        if (import.meta.env.DEV) {
          console.log(
            `🎙️ Audio recorded: ${(audioBlob.size / 1024 / 1024).toFixed(2)}MB`,
          )
        }

        const extension = mimeType.includes("webm") ? "webm" : "mp4"
        const file = new File(
          [audioBlob],
          `recording_${Date.now()}.${extension}`,
          { type: mimeType },
        )

        cleanup()
        setAudioFile(file)
        resolve(file)
      }

      mediaRecorderRef.current.stop()
    })
  }, [cleanup])

  return useMemo((): RecordingState => {
    if (audioFile) {
      return { status: "complete", audioFile, start, reset }
    }

    if (!isSessionActive) {
      return { status: "idle", start }
    }

    if (isPaused) {
      return { status: "paused", resume, levels, stop, accept }
    }

    return { status: "recording", levels, pause, stop, accept }
  }, [
    audioFile,
    isSessionActive,
    isPaused,
    levels,
    start,
    pause,
    resume,
    stop,
    accept,
    reset,
  ])
}
